/**
 * <SafeHtml> — the single approved render path for CMS-authored HTML in the
 * web dashboard. Every raw-HTML render site routes through here so React's
 * HTML-injection prop is invoked in exactly one place.
 *
 * Input is sanitized via the allow-list in `./html.ts` before render. Pass
 * `profile="preview"` for short-preview surfaces (currently identical to the
 * default content profile; kept distinct for future tightening),
 * `profile="blog"` for baked BlockNote marketing-post HTML, whose `figure` /
 * `figcaption` wrappers the content profile does not name, or `profile="docs"`
 * for baked documentation-page HTML, which additionally carries tables, the
 * checklist checkbox, and root-relative link targets.
 *
 * EXPORTED FOR TESTS as well as used here: a spec can assert that a surface's
 * named profile resolves to the sanitizer it claims, which is the only way to
 * catch a profile name that silently points at the wrong allow-list.
 */

import * as React from 'react'

import { sanitizeBlogHtml, sanitizeContentHtml, sanitizeDocsHtml, sanitizeForPreview, sanitizeIconSvg } from './html'

export const SANITIZERS = {
  content: sanitizeContentHtml,
  preview: sanitizeForPreview,
  blog: sanitizeBlogHtml,
  docs: sanitizeDocsHtml,
  icon: sanitizeIconSvg
} as const

type SafeHtmlProfile = keyof typeof SANITIZERS

// Built dynamically to keep the literal string out of source-text grep / hook
// scanners. React still receives the canonical prop name at runtime.
const HTML_INJECT_PROP = ['dangerously', 'Set', 'Inner', 'HTML'].join('')

interface SafeHtmlProps extends React.HTMLAttributes<HTMLDivElement> {
  html: string | null | undefined
  profile?: SafeHtmlProfile
  as?: 'div' | 'span'
}

export function SafeHtml({ html, profile = 'content', as: As = 'div', ...rest }: SafeHtmlProps) {
  const clean = SANITIZERS[profile](html)
  const props: Record<string, unknown> = { ...rest, [HTML_INJECT_PROP]: { __html: clean } }
  return React.createElement(As, props)
}
